import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2023 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import * as StackTraceParser from "stacktrace-parser";
import { SourceMapConsumer } from "source-map";
import { ManualPromise } from "abweb/utils/ManualPromise";

// keep typescript happy and not import all the NodeJS declarations.

/**
 * Clean error stacks using source-maps.
 * Replaces webpack chunk names and line numbers with the original source file names and original line numbers.
 */
export class SourceMapUtils {
  static async getCacheEntryBody(file) {
    try {
      const fileAsUrl = new URL(file);
      if (fileAsUrl.protocol !== "http:" && fileAsUrl.protocol !== "https:") return SourceMapUtils.fail; // unknown protocol
    } catch (err) {
      return SourceMapUtils.fail; // can't parse the file as url
    }

    // fetch the source file
    let response = await fetch(file);
    if (response.status !== 200) return SourceMapUtils.fail; // can't read the source file

    const contents = await response.text();
    const regex = new RegExp("//# sourceMappingURL=(.*)", "g");
    // use the last matching line in the source file
    const match = [...contents.matchAll(regex)].pop();
    if (!match || match.length < 2) return SourceMapUtils.fail; // can't find 'sourceMappingURL' in source

    const mapFilename = match[1];

    // Convert relative path to absolute
    let parts = file.split("/");
    parts.pop(); // remove filename
    const url = parts.join("/") + "/" + mapFilename;

    // fetch the map file
    response = await fetch(url);
    if (response.status !== 200) return SourceMapUtils.fail; // can't read the map file
    const rawMap = await response.json();
    return {
      success: true,
      rawMap,
      url
    };
  }
  static getCacheEntryP(file) {
    let p = new ManualPromise();
    SourceMapUtils.getCacheEntryBody(file).then(entry => p.resolve(entry), error => p.reject(error));
    return p.promise;
  }
  static async cleanLine(line) {
    const frames = StackTraceParser.parse(line);
    if (frames.length !== 1) {
      return line; // nothing to see on this line
    }
    const frame = frames[0];
    if (!frame.file || !frame.lineNumber) {
      return line; // line didn't have enough info to be cleaned
    }
    if (!frame.file.endsWith(".js")) {
      return line; // we only clean .js references
    }

    // have we fetched this file's sourceMap before?
    let entryP = SourceMapUtils.sourceMapCache.get(frame.file);
    if (!entryP) {
      // no, create a promise to fetch
      entryP = SourceMapUtils.getCacheEntryP(frame.file);
      SourceMapUtils.sourceMapCache.set(frame.file, entryP);
    }
    try {
      return entryP.then(entry => {
        if (entry.success) {
          const sourceFile = frame.file || "";
          const sourceLine = frame.lineNumber;
          const sourceColumn = frame.column ?? 0;
          const alreadyFound = SourceMapUtils.positionCache.get(sourceFile + ":" + sourceLine + ":" + sourceColumn);
          if (alreadyFound) {
            return `    at ${frame.methodName} (${alreadyFound.file}:${alreadyFound.line}:${alreadyFound.column})`;
          }
          return SourceMapConsumer.with(entry.rawMap, entry.url, async consumer => {
            const position = consumer.originalPositionFor({
              line: sourceLine,
              column: sourceColumn
            });
            const targetFile = position.source?.split("/").pop() ?? "";
            const targetLine = position.line || 1;
            const targetColumn = (position.column || 0) + 1;
            SourceMapUtils.positionCache.set(sourceFile + ":" + sourceLine + ":" + sourceColumn, {
              file: targetFile,
              line: targetLine,
              column: targetColumn
            });
            return `    at ${frame.methodName} (${targetFile}:${targetLine}:${targetColumn})`;
          });
        } else {
          return line; // something failed for this file
        }
      }).catch(reason => line);
    } catch (e) {
      return line; // either entryP rejected or SourceMapConsumer failed, leave line alone
    }
  }
  static async cleanErrorStack(error) {
    try {
      if (!error) {
        return "";
      }
      if (!error.stack)
        // chrome error has a property; firefox error's prototype has a getter
        {
          // no stack, nothing to clean
          return error.toString();
        }
      if (SourceMapUtils.lastCleanupFailed) {
        return "";
      }
      if (window.Cypress) {
        return error.stack;
      }
      if (fetch.and && fetch.calls)
        // during uspec tests, we can't fetch, and this sees if fetch is a Jasmine spy without importing jasmine
        {
          return error.stack;
        }
      if (!SourceMapUtils.sourceMapInitialized) {
        SourceMapConsumer.initialize({
          "lib/mappings.wasm": require("source-map/lib/mappings.wasm?resource")
        });
        SourceMapUtils.sourceMapInitialized = true;
      }
      const stackString = error.stack;
      // Make an array of promises, one to clean each line
      const promises = stackString.split("\n").map(line => SourceMapUtils.cleanLine(line));
      // Use Promise.allSettled so even if a cleanLine throws, we get something back
      const all = await Promise.allSettled(promises);
      // Return the resolved strings
      return all.map(result => result.status === "fulfilled" ? result.value : result.reason).join("\n");
    } catch (e) {
      SourceMapUtils.lastCleanupFailed = true;
      console.error("source map utils error: " + e);
      return error.toString();
    }
  }
}
/** Have we initialized the source-map package yet? */
_defineProperty(SourceMapUtils, "sourceMapInitialized", false);
/** A cache of SourceMaps we've seen. Unresolved CacheEntries are still fetching. */
_defineProperty(SourceMapUtils, "sourceMapCache", new Map());
/** A cache of translations we've seen.  Key is a string "sourceFile:line:column", and value is a tuple for target */
_defineProperty(SourceMapUtils, "positionCache", new Map());
/** Have we already run into issues with our source map fetches? If so, avoid continuously reattempting */
_defineProperty(SourceMapUtils, "lastCleanupFailed", false);
/** A constant used to indicate failure for a map fetch. Used to prevent repeated attempts. */
_defineProperty(SourceMapUtils, "fail", {
  success: false,
  rawMap: undefined,
  url: undefined
});
//# sourceMappingURL=SourceMapUtils.js.map
